import { get, range, isFinite } from 'lodash-es';
/* Utils */
import { createModelSegment } from 'client/data/luckdragon/segment';
import { CarBuyingAPI, EdmundsAPI } from 'client/data/api/api-client';
import { withMetrics } from 'client/data/api/api-metrics';
import { objectToQueryString } from 'site-modules/shared/utils/string';
import { getDefaultApiOptionsFromContext } from 'site-modules/shared/utils/car-buying/api-helper';
import { WisdomOfCrowdStorage } from 'client/site-modules/wisdom-of-the-crowd/utils/storage';
import { isNode } from 'client/utils/environment';
import { ClientConfig } from 'client/configuration';
import { getSearchResultsQuery } from 'site-modules/shared/utils/inventory/params-conversion';
/* Constants */
import { STORAGE_KEYS } from 'client/site-modules/wisdom-of-the-crowd/constants/storage';
import { FRANCHISE_LIMIT } from 'client/site-modules/shared/constants/new-core';
/* Models */
import { VisitorModel } from './visitor';
import { buildPopularStylesPath } from './vehicle';

function buildMonthlyInventoriesPathWithTrim({ make, model, year, trim, styleId }) {
  return styleId
    ? `makes["${make}"].models["${model}"].years["${year}"].trims["${trim}"].styles["${styleId}"].monthlyInventories`
    : `makes["${make}"].models["${model}"].years["${year}"].trims["${trim}"].monthlyInventories`;
}

function buildMonthlyInventoriesPath({ make, model, year, styleId }) {
  return styleId
    ? `makes["${make}"].models["${model}"].years["${year}"].styles["${styleId}"].monthlyInventories`
    : `makes["${make}"].models["${model}"].years["${year}"].monthlyInventories`;
}

/**
 * Row model paths (represents JSON string)
 */
export const MODEL = {
  IS_DEALS_AVAILABLE: 'isDealsAvailable',
  DELIGHTED_SURVEY_COMPLETED: 'isDelightedSurveyCompleted',
  MONTHLY_INVENTORIES: 'makes["{make}"].models["{model}"].years["{year}"].monthlyInventories',
  MONTHLY_INVENTORIES_BY_STYLE_ID:
    'makes["{make}"].models["{model}"].years["{year}"].styles["{styleId}"].monthlyInventories',
  MONTHLY_INVENTORIES_BY_TRIM: 'makes["{make}"].models["{model}"].years["{year}"].trims["{trim}"].monthlyInventories',
  MONTHLY_INVENTORIES_BY_TRIM_AND_STYLE_ID:
    'makes["{make}"].models["{model}"].years["{year}"].trims["{trim}"].styles["{styleId}"].monthlyInventories',
  CROWD_SOURCED_DEALERS: 'makes["{make}"].models["{model}"].years["{year}"].crowdSourcedDealers',
  STYLES: 'makes["{make}"].models["{model}"].defaultSubmodel.years["{year}"].styles',
  CROWD_SOURCED_WIDGET_PARAMS: 'crowdSourcedWidgetParams',
  CROWD_SOURCED_WIDGET_DEALERS: 'crowdSourcedWidgetDealers',
  CROWD_SOURCED_WIDGET_VINS: 'crowdSourcedWidgetVins',
  CSD_FRANCHISE_INVENTORY: 'makes["{make}"].models["{model}"].years["{year}"].csdFranchiseInventory',
  CSD_PERCENT_OFF: 'makes["{make}"].models["{model}"].years["{year}"].csdPercentOff',
};
/**
 * Path builders
 */
export const PATHS = {
  IS_DEALS_AVAILABLE: MODEL.IS_DEALS_AVAILABLE,
  DELIGHTED_SURVEY_COMPLETED: 'isDelightedSurveyCompleted',
  /**
   * Builds monthly inventories path.
   * @param {string} make
   * @param {string} model
   * @param {string|number} year
   * @param {string} trim
   * @return {string}
   */
  MONTHLY_INVENTORIES: ({ make, model, year, trim, styleId }) => {
    if (!(make && model && year)) return '';

    return trim
      ? buildMonthlyInventoriesPathWithTrim({ make, model, year, trim, styleId })
      : buildMonthlyInventoriesPath({
          make,
          model,
          year,
          styleId,
        });
  },
  /**
   * Builds crowdsourced dealers deals path.
   * @param {string} make
   * @param {string} model
   * @param {string|number} year
   * @return {string}
   */
  CROWD_SOURCED_DEALERS: ({ make, model, year }) =>
    make && model && year ? `makes["${make}"].models["${model}"].years["${year}"].crowdSourcedDealers` : '',
  STYLES: params => {
    const { make, model, year } = params;
    return make && model && year && buildPopularStylesPath(params);
  },
  CROWD_SOURCED_WIDGET_DEALERS: MODEL.CROWD_SOURCED_WIDGET_DEALERS,
  CROWD_SOURCED_WIDGET_VINS: MODEL.CROWD_SOURCED_WIDGET_VINS,
  CROWD_SOURCED_WIDGET_PARAMS: MODEL.CROWD_SOURCED_WIDGET_PARAMS,
  CSD_FRANCHISE_INVENTORY: ({ make, model, year }) =>
    make && model && year ? `makes["${make}"].models["${model}"].years["${year}"].csdFranchiseInventory` : '',
  CSD_PERCENT_OFF: ({ make, model, year }) =>
    make && model && year ? `makes["${make}"].models["${model}"].years["${year}"].csdPercentOff` : '',
};

export const DealsModel = createModelSegment('deals', [
  {
    path: MODEL.IS_DEALS_AVAILABLE,
    async resolve(match, context) {
      try {
        const { zipCode, dma } = await context.resolveValue('location', VisitorModel);
        const options = await getDefaultApiOptionsFromContext(context);
        const params = { dma };
        const url = `/car-buying/v1/zipcodes/${zipCode}/lease-enabled?${objectToQueryString(params)}`;
        const { enabled } = await withMetrics(EdmundsAPI, context).fetchJson(url, options);
        return enabled;
      } catch {
        return false;
      }
    },
  },
  {
    path: MODEL.MONTHLY_INVENTORIES,
    async resolve({ make, model, year }, context) {
      const { zipCode, stateCode } = await context.resolveValue('location', VisitorModel, false);

      const url = `/purchasefunnel/v1/srp/inventory?zip=${zipCode}&stateCode=${stateCode}&make=${make}&model=${model}&year=${year}&inventoryType=new&displayPrice=1-*&sortby=bestDeal:desc&deliveryType=local&productFeature=directDealer&dealType=Great,Good,Fair&pagesize=11&fetchFacets=false&fetchSuggestedFacets=false&seoInfo=false`;

      try {
        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);

        return get(data, 'inventories.results');
      } catch {
        return {};
      }
    },
  },
  {
    path: MODEL.MONTHLY_INVENTORIES_BY_STYLE_ID,
    async resolve({ make, model, year, styleId }, context) {
      const { zipCode, stateCode } = await context.resolveValue('location', VisitorModel, false);

      const url = `/purchasefunnel/v1/srp/inventory?zip=${zipCode}&stateCode=${stateCode}&make=${make}&model=${model}&year=${year}&styleId=${styleId}&inventoryType=new&displayPrice=1-*&sortby=bestDeal:desc&deliveryType=local&productFeature=directDealer&dealType=Great,Good,Fair&pagesize=11&fetchFacets=false&fetchSuggestedFacets=false&seoInfo=false`;

      try {
        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);

        return get(data, 'inventories.results');
      } catch {
        return {};
      }
    },
  },
  {
    path: MODEL.MONTHLY_INVENTORIES_BY_TRIM,
    async resolve({ make, model, year, trim }, context) {
      const { zipCode, stateCode } = await context.resolveValue('location', VisitorModel, false);

      const url = `/purchasefunnel/v1/srp/inventory?zip=${zipCode}&stateCode=${stateCode}&make=${make}&model=${model}&trim=${trim}&year=${year}&inventoryType=new&displayPrice=1-*&sortby=bestDeal:desc&deliveryType=local&productFeature=directDealer&dealType=Great,Good,Fair&pagesize=11&fetchFacets=false&fetchSuggestedFacets=false&seoInfo=false`;

      try {
        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);

        return get(data, 'inventories.results');
      } catch {
        return [];
      }
    },
  },
  {
    path: MODEL.MONTHLY_INVENTORIES_BY_TRIM_AND_STYLE_ID,
    async resolve({ make, model, year, trim, styleId }, context) {
      const { zipCode, stateCode } = await context.resolveValue('location', VisitorModel, false);

      const url = `/purchasefunnel/v1/srp/inventory?zip=${zipCode}&stateCode=${stateCode}&make=${make}&model=${model}&trim=${trim}&year=${year}&styleId=${styleId}&inventoryType=new&displayPrice=1-*&sortby=bestDeal:desc&deliveryType=local&productFeature=directDealer&dealType=Great,Good,Fair&pagesize=11&fetchFacets=false&fetchSuggestedFacets=false&seoInfo=false`;

      try {
        const data = await withMetrics(EdmundsAPI, context).fetchJson(url);

        return get(data, 'inventories.results');
      } catch {
        return [];
      }
    },
  },
  {
    path: MODEL.DELIGHTED_SURVEY_COMPLETED,
    async resolve() {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return Promise.resolve(
        WisdomOfCrowdStorage.getValueFromLocalStorage(STORAGE_KEYS.DELIGHTED_SURVEY_COMPLETED) || false
      );
    },
    update(value) {
      WisdomOfCrowdStorage.setValueToLocalStorage(STORAGE_KEYS.DELIGHTED_SURVEY_COMPLETED, value);
      return Promise.resolve(value);
    },
  },
  {
    path: MODEL.CROWD_SOURCED_DEALERS,
    async resolve({ make, model, year }, context) {
      const options = {
        method: 'GET',
        retries: 0,
        headers: {
          'content-type': 'application/json',
          'x-api-key': ClientConfig.get('carBuying').xApiKey,
        },
        timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
      };

      const { dma } = await context.resolveValue('location', VisitorModel);

      try {
        const crowdSourcedDealAPIUrl = `/api/v1/crowd-sourced-deals/${make}/${model}/${year}?dma=${dma}`;
        const response = await CarBuyingAPI.fetchJson(crowdSourcedDealAPIUrl, options);

        return response;
      } catch {
        return [];
      }
    },
  },
  {
    path: MODEL.STYLES,
    async resolve({ make, model, year }, context) {
      const { zipCode } = await context.resolveValue('location', VisitorModel, false);
      try {
        const [{ results }, aggregateStyles] = await Promise.all([
          withMetrics(EdmundsAPI, context).fetchJson(`/vehicle/v4/makes/${make}/models/${model}/years/${year}/styles`),
          withMetrics(EdmundsAPI, context).fetchJson(
            `/inventory/v5/aggregate?make=${make}&model=${model}&year=${year}&zip=${zipCode}&top=30&radius=50&type=NEW&deliveryType=local&by=vehicleInfo.styleInfo.styleId:thirdPartyInfo.priceValidation.dealType`
          ),
        ]);
        const availableStyles = get(aggregateStyles, 'vehicleInfo.styleInfo.styleId', {});

        return results.filter(
          style =>
            availableStyles[style.id] &&
            availableStyles[style.id].count > 0 &&
            (!!availableStyles[style.id]['thirdPartyInfo.priceValidation.dealType'].Great ||
              !!availableStyles[style.id]['thirdPartyInfo.priceValidation.dealType'].Good)
        );
      } catch {
        return [];
      }
    },
  },
  {
    path: MODEL.CROWD_SOURCED_WIDGET_PARAMS,
  },
  {
    /**
     * If set chart_mode=dealer then collect data from /inventory/v5/dealerCards
     */
    path: MODEL.CROWD_SOURCED_WIDGET_DEALERS,
    async resolve(match, context) {
      const options = {
        method: 'GET',
        retries: 0,
        headers: {
          'content-type': 'application/json',
        },
        timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
      };

      const {
        displayBasic = false,
        pageSize = 100,
        setChartGeo,
        make,
        model,
        year,
        trim,
        styleId,
      } = (await context.resolveValue(PATHS.CROWD_SOURCED_WIDGET_PARAMS)) || {};
      const { zipCode, dma, stateCode } = await context.resolveValue('location', VisitorModel);

      let results;
      let response;
      const fields = 'vin,computedInfo.savingsPercent,dealerInfo(address,rooftopId,franchiseId,name,distance)';
      const radius = setChartGeo === 'state' ? '200' : '500';
      const query = getSearchResultsQuery({
        zip: zipCode,
        userDma: setChartGeo === 'dma' ? dma : null,
        radius: setChartGeo !== 'dma' ? radius : null,
        make,
        model,
        year,
        trim,
        styleId,
      });
      // /inventory/v5/dealerCards can search only for 1st page by sort with sortby=avg_computedInfo.savingsPercent
      const crowdSourcedDealAPIUrl = (pageNum = 1) =>
        `/inventory/v5/dealerCards?${query}&type=NEW&${
          displayBasic ? 'searchable=true,false&-dealerInfo.dealerRelation=RESTRICTED&' : ''
        }displayPrice=1-*&pageSize=${pageSize}&pageNum=${pageNum}&view=DEFAULT_SRP&subAggregates=discountOffMsrp&categories=computedInfo.savingsPercent:desc&fields=${fields}&sortby=avg_computedInfo.savingsPercent:desc`;

      try {
        response = await EdmundsAPI.fetchJson(crowdSourcedDealAPIUrl(), options);
      } catch {
        return [];
      }
      results = response['dealerInfo.franchiseId'].results;
      // filter records for avg_computedInfo_savingsPercent values
      results = results.filter(data => isFinite(get(data, 'subAggregates.avg_computedInfo_savingsPercent')));

      if (setChartGeo === 'state') {
        results = results.filter(
          data => get(data['computedInfo.savingsPercent_desc'][0], 'dealerInfo.address.stateCode') === stateCode
        );
      }

      /*
       * reduce dealer data to simple object
       * */
      return results.reduce((acc, data) => {
        const dealerData = get(data, 'subAggregates', {});
        const inventoryCount = get(dealerData, 'count');
        const minPrice = get(dealerData, ['min_prices.displayPrice']);
        const savingsPercent = get(dealerData, 'avg_computedInfo_savingsPercent', 0);
        const {
          dealerInfo: { rooftopId, franchiseId, name, distance },
        } = data['computedInfo.savingsPercent_desc'][0];

        acc.push({
          dealerBestPrice: minPrice,
          matchingInventory: inventoryCount,
          percentOffMsrp: savingsPercent,
          rooftopId: parseInt(rooftopId, 10),
          franchiseId: parseInt(franchiseId, 10),
          name,
          distance,
        });
        return acc;
      }, []);
    },
  },
  {
    /**
     * If set chart_mode=vin then collect data from /inventory/v5/find and sort by computedInfo.savingsPercent
     */
    path: MODEL.CROWD_SOURCED_WIDGET_VINS,
    async resolve(match, context) {
      const options = {
        method: 'GET',
        retries: 0,
        headers: {
          'content-type': 'application/json',
        },
        timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
      };

      const {
        displayBasic = false,
        pageSize = 100,
        setChartGeo,
        maxCalls = 4,
        make,
        model,
        year,
        trim,
        styleId,
      } = (await context.resolveValue(PATHS.CROWD_SOURCED_WIDGET_PARAMS)) || {};
      const { zipCode, dma, stateCode } = await context.resolveValue('location', VisitorModel);

      let totalPages = 0;
      let results;
      let response;
      const fields = 'vin,computedInfo.savingsPercent,dealerInfo(address,rooftopId,franchiseId,name,distance)';
      const radius = setChartGeo === 'state' ? '200' : '500';
      const query = getSearchResultsQuery({
        zip: zipCode,
        userDma: setChartGeo === 'dma' ? dma : null,
        radius: setChartGeo !== 'dma' ? radius : null,
        make,
        model,
        year,
        trim,
        styleId,
      });
      const crowdSourcedDealAPIUrl = (pageNum = 1) =>
        `/inventory/v5/find?${query}&type=NEW&${
          displayBasic ? '-dealerInfo.dealerRelation=RESTRICTED&' : 'dealerInfo.dealerRelation=PARTNER&'
        }displayPrice=1-*&pageSize=${pageSize}&pageNum=${pageNum}&view=DEFAULT_SRP&fields=${fields}&sortby=computedInfo.savingsPercent:desc`;

      try {
        response = await EdmundsAPI.fetchJson(crowdSourcedDealAPIUrl(), options);
      } catch {
        return [];
      }
      totalPages = response.totalPages;
      results = response.results;

      // /inventory/v5/find max result records count is 10.000 by ElasticSearch limitation
      if (totalPages > 1 && maxCalls > 1) {
        totalPages = totalPages > 100 ? 100 : totalPages;
        const callsCount = Math.min(totalPages, maxCalls);
        let combinedResponse = [];
        try {
          combinedResponse = await Promise.all(
            [...range(2, callsCount), callsCount]
              .filter(Boolean)
              .map(pageNum => EdmundsAPI.fetchJson(crowdSourcedDealAPIUrl(pageNum), options))
          );
        } finally {
          combinedResponse = combinedResponse.reduce((acc, resp) => [...acc, ...resp.results], []);
        }
        results = [...results, ...combinedResponse];
      }

      if (setChartGeo === 'state') {
        results = results.filter(data => get(data, 'dealerInfo.address.stateCode') === stateCode);
      }

      /*
       * reduce vin data to simple object
       * */
      return results.reduce((acc, data) => {
        const {
          computedInfo: { savingsPercent = 0 } = {},
          dealerInfo: { rooftopId, franchiseId, name, distance },
          vin,
        } = data;

        acc.push({
          vin,
          percentOffMsrp: savingsPercent,
          rooftopId: parseInt(rooftopId, 10),
          franchiseId: parseInt(franchiseId, 10),
          name,
          distance,
        });
        return acc;
      }, []);
    },
  },
  {
    path: MODEL.CSD_FRANCHISE_INVENTORY,
    async resolve({ make, model, year }, context) {
      const { stateCode } = await context.resolveValue('location', VisitorModel);
      const url = `/car-buying/v1/csd-franchise-inventory?make=${make}&model=${model}&year=${year}&state=${stateCode?.toLowerCase()}&franchisecountlimit=${FRANCHISE_LIMIT}`;
      const options = {
        method: 'GET',
        retries: 0,
        headers: {
          'content-type': 'application/json',
        },
        timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
        showAPIError: true,
      };

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url, options)
        .then(res => res.reduce((acc, data) => [...acc, { ...data?.franchise }], []))
        .catch(() => []);
    },
  },
  {
    path: MODEL.CSD_PERCENT_OFF,
    async resolve({ make, model, year }, context) {
      const { stateCode } = await context.resolveValue('location', VisitorModel);
      const url = `/car-buying/v1/csd-ymm-state?make=${make}&model=${model}&year=${year}`;
      const options = {
        method: 'GET',
        retries: 0,
        headers: {
          'content-type': 'application/json',
        },
        timeout: ClientConfig.get('carBuying').apiDefaultTimeoutMs,
        showAPIError: true,
      };

      return withMetrics(EdmundsAPI, context)
        .fetchJson(url, options)
        .then(res => res.find(item => item?.stateCode === stateCode))
        .catch(() => {});
    },
  },
]);
